import posthog from 'posthog-js';
import { fbpFromCookie } from './attribution';

export type ConversionType = 'sign_up' | 'start_trial' | 'purchase' | 'upgrade';

/**
 * Onboarding funnel instrumentation.
 *
 * Every event goes BOTH to PostHog (funnels, session analysis) and to the GTM
 * dataLayer (ad-platform wiring) so neither destination loses visibility.
 * Event names use snake_case, e.g. `onboarding_step_viewed`.
 */
export const trackOnboarding = (event: string, props: Record<string, any> = {}) => {
  const payload = {
    flow: 'onboarding',
    entry_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...props,
  };

  try {
    posthog.capture(event, payload);
  } catch {
    /* analytics must never break the funnel */
  }

  try {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event, ...payload });
    }
  } catch {
    /* analytics must never break the funnel */
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[Onboarding] ${event}`, payload);
  }
};

interface ConversionData {
  event: ConversionType;
  value?: number;
  currency?: string;
  plan_name?: string;
  previous_plan?: string;
  user_id?: string;
  transaction_id?: string;
  interval?: 'month' | 'year';
}

// LTV multipliers - adjust based on your actual retention data
const LTV_MULTIPLIERS = {
  month: 6,    // 6 months average retention
  year: 1.5,   // 1.5 years average retention
};

/**
 * Calculate Lifetime Value from actual charged amount
 */
export const calculateLTV = (amount: number, interval: 'month' | 'year'): number => {
  const multiplier = LTV_MULTIPLIERS[interval] || 6;
  return Math.round(amount * multiplier * 100) / 100;
};

/**
 * Push conversion event to dataLayer for GTM
 */
export const trackConversion = (data: ConversionData) => {
  if (typeof window === 'undefined') return;

  let conversionValue = data.value || 0;
  if (data.value && data.interval) {
    conversionValue = calculateLTV(data.value, data.interval);
  }

  // Mirror to PostHog as well as the dataLayer. Conversions used to be pushed to
  // GTM only, which left PostHog blind to the single event every signup
  // experiment is judged on: the moment a card is actually handed over. Without
  // this, an experiment can measure who reached the plans step and who skipped
  // it, but not who converted. Fired before the dataLayer push so a missing GTM
  // container cannot swallow the PostHog event.
  try {
    posthog.capture(data.event, {
      conversion_value: conversionValue,
      original_amount: data.value || 0,
      currency: data.currency || 'USD',
      plan_name: data.plan_name,
      previous_plan: data.previous_plan,
      transaction_id: data.transaction_id,
      billing_interval: data.interval,
    });
  } catch {
    /* analytics must never break the payment confirmation screen */
  }

  if (!window.dataLayer) return;

  window.dataLayer.push({
    event: data.event,
    conversion_value: conversionValue,
    original_amount: data.value || 0,
    currency: data.currency || 'USD',
    plan_name: data.plan_name,
    previous_plan: data.previous_plan,
    user_id: data.user_id,
    transaction_id: data.transaction_id,
    billing_interval: data.interval,
  });
  
  console.log(`[Analytics] Tracked: ${data.event}, Value: $${conversionValue}`);
};

/**
 * Rebuild an fbc from a stored fbclid when the `_fbc` cookie was never readable
 * (e.g. the click id came from the URL). Uses the moment we first stored the
 * click, which is as close to the real click time as we have.
 */
const fbcFromStoredClick = (): string | null => {
  const fbclid = localStorage.getItem('fbclid');
  if (!fbclid) return null;
  const clickedAt = localStorage.getItem('fbclid_timestamp') || Date.now().toString();
  return `fb.1.${clickedAt}.${fbclid}`;
};

/**
 * Get stored tracking params for passing to backend
 */
export const getTrackingParams = () => {
  if (typeof window === 'undefined') return {};
  
  return {
    gclid: localStorage.getItem('gclid') || null,
    fbclid: localStorage.getItem('fbclid') || null,
    fbc: localStorage.getItem('fbc') || fbcFromStoredClick(),
    fbp: localStorage.getItem('fbp') || fbpFromCookie(),
    utm_source: localStorage.getItem('utm_source') || null,
    utm_medium: localStorage.getItem('utm_medium') || null,
    utm_campaign: localStorage.getItem('utm_campaign') || null,
  };
};

/**
 * Determine if this is a NEW subscription (not an upgrade or plan change)
 */
export const isNewSubscription = (previousPlan: string | null, newPlan: string): boolean => {
  const freePlans = ['maayot Free', '', null, undefined];
  const isPreviousFree = freePlans.includes(previousPlan as any);
  const isNewPaid = !freePlans.includes(newPlan);
  return isPreviousFree && isNewPaid;
};

/**
 * Determine if this is an upgrade (tier increase)
 */
export const isUpgrade = (previousPlan: string | null, newPlan: string): boolean => {
  const planHierarchy: Record<string, number> = { 
    'maayot Free': 0, 
    'maayot Standard': 1, 
    'maayot Pro': 2 
  };
  const prevRank = planHierarchy[previousPlan || 'maayot Free'] ?? 0;
  const newRank = planHierarchy[newPlan] ?? 0;
  return newRank > prevRank && prevRank > 0; // Must be upgrading FROM a paid plan
};









export type WriteSpeakEvent =
  | 'write_speak_viewed'
  | 'recording_started'
  | 'recording_completed'
  | 'correction_sent'
  | 'pro_gate_hit';

/**
 * Writing & Speaking step events (web). Names match the native apps' events so
 * the dashboards can combine platforms; PostHog's $lib tells them apart.
 */
export const trackWriteSpeak = (event: WriteSpeakEvent, props: Record<string, any> = {}) => {
  try {
    posthog.capture(event, { surface: 'write_speak_step', ...props });
  } catch {
    /* analytics must never break the step */
  }
};
